<template>
  <AppCard elevation="0" :class="{ 'text-grey-400': isExpiredPack }">
    <div class="d-flex align-center mb-3">
      <!-- Title -->
      <p class="mb-0 text--medium text--bold">
        {{ packTitle }}
        <AppInfoTooltip>
          <p class="font-weight-semi-bold">{{ $t('subscriptions.views.SubscriptionConsumption.DataroomStorage.details') }}</p>
          <p>
            {{ $t('subscriptions.views.SubscriptionConsumption.DataroomStorage.subscriptionDate') }}
            {{ ISOToDDMMYYYY(pack.from) }}
          </p>
          <p v-if="packDuration.years || packDuration.months">
            {{ $t('subscriptions.views.SubscriptionConsumption.Packs.validityDuration.title') }}
            <i18n-t v-if="packDuration.years"
                    keypath="subscriptions.views.SubscriptionConsumption.Packs.validityDuration.years"
                    :plural="packDuration.years"
                    tag="span"
                    scope="global"
                    class="mr-1"
            >
              <template #duration>
                {{ packDuration.years }}
              </template>
            </i18n-t>
            <i18n-t v-if="packDuration.months"
                    keypath="subscriptions.views.SubscriptionConsumption.Packs.validityDuration.months"
                    :plural="packDuration.months"
                    tag="span"
                    scope="global"
            >
              <template #duration>
                {{ packDuration.months }}
              </template>
            </i18n-t>
          </p>
          <p>
            {{ $t('subscriptions.views.SubscriptionConsumption.DataroomStorage.endDate') }}
            {{ ISOToDDMMYYYY(pack.to) }}
          </p>
        </AppInfoTooltip>
      </p>

      <!-- Expired -->
      <v-chip v-if="isExpiredPack" class="ml-2" label>
        <p class="mb-0 text--small text--bold">
          {{ $t('subscriptions.views.SubscriptionConsumption.Packs.expired') }}
        </p>
      </v-chip>

      <!-- Share Button -->
      <app-button class="ml-auto"
                  type="icon"
                  @click="openShareSubscriptionConsumptionDialog()"
      >
        <app-material-icon name="share" type="fa" size="sm" weight="fal" />
      </app-button>
    </div>

    <!-- Subtitle -->
    <p v-if="!isExpiredPack" class="mb-0 text--x-small">
      {{ $t('subscriptions.views.SubscriptionConsumption.Packs.subTitle') }}
    </p>

    <div class="my-6">
      <div class="d-flex align-center mb-1">
        <div class="text-grey-700">
          {{ pack.consumed }}
          <i18n-t :keypath="`subscriptions.views.SubscriptionConsumption.Packs.${isSignaturePack ? 'signaturesUsed' : 'envelopesUsed'}`"
                  tag="span"
                  :plural="pack.consumed"
          />
          {{ pack.total }}
        </div>
      </div>

      <v-progress-linear :model-value="progressBarValue"
                         :color="DATA_VISUALISATION_COLORS[`${isExpiredPack ? 'Grey' : 'Indigo'}-200`]"
                         bg-color="grey-300"
                         height="20"
                         rounded>
      </v-progress-linear>
    </div>

    <!-- DIALOGS -->
    <ShareSubscriptionConsumptionDialog v-if="shareSubscriptionConsumptionDialogIsOpen"
                                        :title="shareSubscriptionConsumptionDialogTitle"
                                        :submit-action="SHARE_SUBSCRIPTION_CONSUMPTION_PACK"
                                        :action-params="{ packId: pack.id }"
                                        :ok-loading="shareSubscriptionConsumptionPackPending"
                                        @close="closeShareSubscriptionConsumptionDialog"
    />
  </AppCard>
</template>

<script>
  import dayjs from 'dayjs'
  import { mapActions, mapState } from "vuex";

  import AppCard from '@/common/AppCard.vue'
  import AppInfoTooltip from '@/common/AppInfoTooltip.vue'
  import { DATA_VISUALISATION_COLORS } from '@/common/utils/colors'
  import { ISOToDDMMYYYY } from '@/common/utils/dates'
  import { SHARE_SUBSCRIPTION_CONSUMPTION_PACK } from "@/store/modules/subscription/action_types";
  import ShareSubscriptionConsumptionDialog from "@/subscriptions/views/ShareSubscriptionConsumptionDialog.vue";

  export default {
    name: 'SubscriptionConsumptionPacksTemplate',
    components: { ShareSubscriptionConsumptionDialog, AppCard, AppInfoTooltip },
    props: {
      pack: {
        type: Object,
        required: true,
      },
      isSignaturePack: {
        type: Boolean,
        default: false,
      },
    },
    data () {
      return {
        ISOToDDMMYYYY,
        DATA_VISUALISATION_COLORS,
        shareSubscriptionConsumptionDialogIsOpen: false,
      }
    },
    computed: {
      ...mapState('subscription', ['shareSubscriptionConsumptionPackPending']),
      packTitle () {
        const type = this.isExpiredPack ? 'mixed' : this.pack.type;
        const packCategory = this.isSignaturePack ? 'Signature' : 'Envelope';
        return this.$t(`subscriptions.views.SubscriptionConsumption.Packs.${type}${packCategory}Pack`);
      },
      shareSubscriptionConsumptionDialogTitle() {
        return this.$t('subscriptions.views.SubscriptionConsumption.ShareDialog.title', { itemName: this.packTitle })
      },
      packDuration() {
        if (!this.pack.from || !this.pack.to) return {}
        const start = dayjs(this.pack.from);
        const end = dayjs(this.pack.to);

        const years = end.diff(start, 'year');
        const months = end.diff(start.add(years, 'year'), 'month');

        return {
          years,
          months,
        }
      },
      isExpiredPack () {
        return this.pack.type === 'expired'
      },
      progressBarValue () {
        return (this.pack.consumed / this.pack.total) * 100
      },
    },
    methods: {
      ...mapActions('subscription', [SHARE_SUBSCRIPTION_CONSUMPTION_PACK]),
      openShareSubscriptionConsumptionDialog () {
        this.shareSubscriptionConsumptionDialogIsOpen = true
      },
      closeShareSubscriptionConsumptionDialog () {
        this.shareSubscriptionConsumptionDialogIsOpen = false
      },
    },
  }
</script>
